/* eslint-disable react/no-unescaped-entities */
/**
 * /specialites/corporate-m-a — Page éditoriale CONFILEX dédiée au M&A.
 *
 * Positionnement strict :
 *   CONFILEX est un moteur d'**orientation institutionnelle** vers des avocats
 *   et professionnels indépendants spécialisés en Corporate / M&A. Aucune
 *   consultation, aucun avis juridique, aucune mise en avant nominative
 *   d'avocats sans leur consentement.
 *
 * Réutilisable comme template pour d'autres spécialités (fiscal, social,
 * immobilier, etc.) en remplaçant l'objet `SPEC` ci-dessous.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Briefcase,
  ShieldCheck,
  Scale,
  Sparkles,
  TrendingUp,
  Banknote,
  Globe2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo from "@/components/Seo";

const SPEC = {
  eyebrow: "CONFILEX · ORIENTATION INSTITUTIONNELLE",
  family: "Droit des sociétés / M&A",
  title: "Orientation vers des spécialistes en Corporate / M&A",
  subtitle:
    "Fusions-acquisitions, restructurations, levées de fonds, opérations transfrontalières : CONFILEX qualifie votre dossier et l'oriente vers des professionnels indépendants pertinents au regard de la complexité, du secteur et de la juridiction concernée.",
  pillars: [
    {
      icon: Building2,
      title: "Croissance externe & fusions",
      copy:
        "Acquisitions stratégiques, fusions horizontales, prises de contrôle minoritaires ou majoritaires — chaque opération mobilise des compétences distinctes en due diligence, structuration capitalistique et négociation.",
    },
    {
      icon: TrendingUp,
      title: "Capital-investissement (Private Equity)",
      copy:
        "Entrées au capital, LBO, opérations secondaires, pactes d'actionnaires : la culture transactionnelle requise diffère sensiblement d'un avocat M&A généraliste à un spécialiste PE confirmé.",
    },
    {
      icon: Banknote,
      title: "Financements alternatifs",
      copy:
        "Prêts non bancaires, dette mezzanine, obligations convertibles, financements unitranche : qualifier le bon interlocuteur dépend du montage et du profil de la contrepartie.",
    },
    {
      icon: Globe2,
      title: "Opérations transfrontalières",
      copy:
        "Une transaction franco-suisse, européenne ou intercontinentale appelle des compétences combinées : droit local, droit fiscal applicable, conformité réglementaire sectorielle.",
    },
    {
      icon: Scale,
      title: "Restructurations & sorties",
      copy:
        "Cessions partielles, apports d'actifs, scissions, privatisations, sorties de pactes : ces opérations exigent une grande maîtrise des conséquences fiscales et sociales associées.",
    },
    {
      icon: Briefcase,
      title: "Marchés de capitaux",
      copy:
        "Introduction en bourse, augmentations de capital, offres publiques, placements privés : la pratique requise diffère selon la place de cotation et la typologie d'investisseurs visés.",
    },
  ],
  why: [
    "CONFILEX n'est pas un cabinet d'avocats. Nous ne rendons aucun avis juridique, fiscal ou financier.",
    "Notre rôle est d'analyser votre demande, de qualifier votre dossier, d'identifier les compétences requises et d'orienter vers des professionnels indépendants pertinents.",
    "Chaque spécialiste présenté reste libre d'accepter ou de refuser la mission. Aucun classement, aucune notation, aucune garantie de résultat n'est affichée.",
  ],
};


export default function SpecialiteCorporateMA() {
  return (
    <div className="min-h-screen bg-ivory">
      <Seo
        title="Corporate / M&A — Orientation institutionnelle CONFILEX vers des spécialistes en droit des sociétés et fusions-acquisitions"
        description="CONFILEX qualifie les dossiers Corporate, M&A, Private Equity, financements alternatifs et opérations transfrontalières et oriente vers des spécialistes indépendants pertinents. Mission de Recherche Premium d'Spécialiste 89 € TTC."
        canonical="/specialites/corporate-m-a"
        keywords={[
          "corporate m&a", "fusion acquisition", "private equity",
          "droit des sociétés", "levée de fonds", "ipo", "due diligence",
          "restructuration", "carve-out", "lbo", "orientation avocat m&a",
        ]}
      />

      {/* Hero */}
      <section className="border-b border-night/10 bg-paper">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-3" data-testid="spec-eyebrow">
            {SPEC.eyebrow}
          </p>
          <h1
            className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-night leading-[1.05] mb-5 max-w-4xl"
            data-testid="spec-title"
          >
            {SPEC.title}
          </h1>
          <p className="text-base sm:text-lg text-night/70 max-w-3xl leading-relaxed mb-7">
            {SPEC.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild className="rounded-full bg-night text-paper hover:bg-night/90 h-12 px-6" data-testid="spec-cta-primary">
              <Link to="/orientation-intelligente">
                <Sparkles className="w-4 h-4 mr-2" /> Démarrer mon orientation
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-6" data-testid="spec-cta-secondary">
              <Link to="/missions/new">
                Soumettre un dossier détaillé <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
          <p className="text-[12px] text-night/55 italic mt-5 max-w-3xl">
            Si aucun spécialiste abonné ne correspond précisément à votre besoin, CONFILEX peut lancer pour vous une <strong>Mission de Recherche Premium d&apos;Spécialiste</strong> (89 € TTC) — recherche, qualification et transmission sécurisée du dossier à au moins 4 profils potentiellement pertinents.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-3">Domaines couverts</p>
        <h2 className="font-serif text-3xl md:text-4xl text-night mb-10 max-w-3xl leading-tight">
          Les six lectures d&apos;une opération M&A.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SPEC.pillars.map((p) => (
            <Card
              key={p.title}
              className="p-6 rounded-2xl border border-slate-100 bg-white hover:border-copper/40 transition-smooth"
              data-testid={`spec-pillar-${p.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="w-10 h-10 rounded-xl bg-ivory flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5 text-copper" />
              </div>
              <h3 className="font-serif text-lg text-night mb-2">{p.title}</h3>
              <p className="text-sm text-night/70 leading-relaxed">{p.copy}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="bg-paper border-y border-night/10">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-3">Pourquoi CONFILEX</p>
              <h2 className="font-serif text-3xl md:text-4xl text-night mb-5 leading-tight">
                Un moteur d&apos;orientation, jamais un cabinet.
              </h2>
              <p className="text-sm text-night/75 leading-relaxed">
                Contrairement à un répertoire généraliste, CONFILEX ne présente pas une liste indifférenciée d&apos;avocats. Chaque demande fait l&apos;objet d&apos;une analyse individualisée, d&apos;une qualification documentaire et d&apos;une sélection ciblée en fonction de la problématique exposée.
              </p>
            </div>
            <ul className="space-y-3" data-testid="spec-why-list">
              {SPEC.why.map((line, i) => (
                <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-100">
                  <ShieldCheck className="w-4 h-4 text-night/70 mt-0.5 shrink-0" />
                  <p className="text-sm text-night/80 leading-relaxed">{line}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Premium Mission CTA */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <Card className="p-8 lg:p-10 rounded-3xl border-[#C9A961]/30 bg-white">
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-2">Prestation CONFILEX</p>
              <h3 className="font-serif text-2xl md:text-3xl text-night mb-2">
                Mission de Recherche Premium d&apos;Expert · 89 € TTC
              </h3>
              <p className="text-sm text-night/70 max-w-2xl leading-relaxed">
                Analyse de votre demande, qualification documentaire, identification des compétences requises, recherche et sélection de plusieurs profils, transmission sécurisée du dossier et suivi de la prise de contact. Le montant rémunère exclusivement la prestation CONFILEX.
              </p>
            </div>
            <Button asChild className="rounded-full bg-night text-paper hover:bg-night/90 h-12 px-6 shrink-0" data-testid="spec-cta-premium">
              <Link to="/orientation">Lancer la mission <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </Card>
      </section>

      {/* Bloc de protection obligatoire */}
      <section className="border-t border-night/10 bg-night text-paper">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#C9A961] mb-3">Mention obligatoire CONFILEX</p>
          <div className="text-sm leading-relaxed text-paper/85 max-w-4xl space-y-3" data-testid="spec-protection-block">
            <p>
              CONFILEX réalise une analyse documentaire, factuelle et organisationnelle des éléments transmis.
            </p>
            <p>
              Le présent rapport ne constitue ni une consultation juridique, ni un avis juridique, ni une consultation comptable, fiscale, notariale ou financière.
            </p>
            <p>
              Toute interprétation professionnelle doit être appréciée par un professionnel compétent au regard de l&apos;ensemble des circonstances du dossier.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
